import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { Event } from '@api/models/event/event';
import { UrlService } from '@utils/services/url/url.service';
import { IMAGE_RESOLUTION } from '@api/models/image/image-resolution';
import { ImageSubType } from '@api/models/image/image-type';
import { IUser } from '@api/models/user/user';
import { GameListService } from '@components/games/services/game-list.service';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import { LayoutService } from '@utils/services/layout/layout.service';

@Component({
  selector: 'lib-games-list',
  templateUrl: './games-list.component.html',
  styleUrls: ['./games-list.component.scss'],
  providers: [GameListService],
})
export class GamesListComponent implements OnInit, OnChanges {
  @Input() @HostBinding('class.inline') public inline = false;
  @Input() public condensed = false;
  @Input() public large = false;
  @Input() public spaced = false;
  @Input() public live = false;
  @Input() public lazyLoadImages = true;
  @Input() public showAds = false;
  @Input() public canScroll = false;
  @Input() public showLeagueInfo = false;
  @Input() public teamNameCentered = true;
  @Input() public sortByRound = false;
  @Input() public sortByLive = false;
  @Input() public sortByLeague = false;
  @Input() public groupByDate = false;
  @Input() public openLinksInNewTab = true;
  @Input() public useRelativeLinks = false;
  @Input() public hideShadow = false;
  @Input() public highlightOnHover = false;
  @Input() public showDate = true;
  @Input() public showRound = false;
  @Input() public showStatus = false;
  @Input() public showReporterStatus = false;
  @Input() public showImages = true;
  @Input() public useAbbreviations = false;
  @Input() public logosOnOutside = false;
  @Input() public displayStatusBackground = true;
  @Input() public showLiveIndicator = false;
  @Input() public loading = false;
  @Input() public limit: number;
  @Input() public infoBackground: string;
  @Input() public highlightGames: string[] = [];
  @Input() public matchLinkPath = 'spiele';
  @Input() public user: IUser;
  @Input() public events: Partial<Event>[];
  @Input() public colorScheme: ColorScheme;
  @Input() public useLink = true;
  @Input() public showDownloads = false;
  @Output() public gameListItemClick = new EventEmitter<Event>();

  public adPositions: string[] = [];
  public logoSize = IMAGE_RESOLUTION.LOW;
  public logoType = ImageSubType.LOGO;

  constructor(
    public gameListService: GameListService,
    @Inject(PLATFORM_ID) protected platformId: any,
    private layoutService: LayoutService
  ) {}

  public ngOnInit(): void {
    if (this.layoutService.darkMode && !this.colorScheme) {
      this.colorScheme = ColorScheme.Dark;
    }
    this.loading = true;
    if (this.inline) {
      this.spaced = true;
      this.condensed = true;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.events && changes.events.currentValue) {
      this.loading = false;
      this.handleChanges();
    }
  }

  public handleChanges(): void {
    if (this.events?.length === 0) {
      return;
    }
    this.gameListService.init(
      this.events,
      this.sortByRound,
      this.sortByLeague,
      this.sortByLive,
      this.groupByDate,
      this.limit
    );
    if (this.showAds) {
      this.setAdPositions();
    }
  }

  public getLeagueLink(readableId: string): string {
    return UrlService.getSubdomainUrl(readableId);
  }

  public showAd(roundIndex: number, gameIndex: number): boolean {
    return this.adPositions.includes(roundIndex + '-' + gameIndex);
  }

  private setAdPositions(): void {
    let gamesCount = 0;
    this.gameListService.eventGroups.forEach((eventGroup, roundIndex) => {
      eventGroup.events.forEach((_, gameIndex) => {
        if (this.adShouldBeShown(gamesCount)) {
          this.adPositions.push(roundIndex + '-' + gameIndex);
        }
        gamesCount++;
      });
    });
  }

  private adShouldBeShown(index: number): boolean {
    if (index === 0) {
      return true;
    }
    if (index > 1) {
      return index % 8 === 0;
    }
  }
}
